import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { profileStore } from "@/lib/profileStore";
import { useAuth } from "@/context/AuthContext";

const ProfileContext = createContext(null);

/**
 * Phase 9 — active viewing profile state.
 *
 * - On auth change: fetches /api/me/profiles. Hydrates `activeProfile` from
 *   localStorage if the saved id matches one of the returned profiles.
 *   Otherwise keeps it null (subscriber must explicitly pick on /select-profile).
 * - `switchProfile(id)` validates the id exists in the list, persists it,
 *   updates state synchronously so api interceptor picks it up next call.
 * - `clearActiveProfile()` clears localStorage + state. Called on logout +
 *   "Switch Profile" menu item.
 */
export function ProfileProvider({ children }) {
  const { user, status } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const lastUserIdRef = useRef(null);

  const fetchProfiles = useCallback(async () => {
    if (status !== "authenticated" || !user) {
      setProfiles([]);
      setActiveProfile(null);
      return [];
    }
    setLoading(true);
    try {
      const { data } = await api.get("/me/profiles");
      setProfiles(data || []);
      // Hydrate activeProfile from localStorage if id still valid
      const savedId = profileStore.get();
      if (savedId) {
        const match = (data || []).find((p) => p.id === savedId);
        if (match) {
          setActiveProfile(match);
        } else {
          profileStore.clear();
          setActiveProfile(null);
        }
      }
      return data || [];
    } catch (_e) {
      return [];
    } finally {
      setLoading(false);
    }
  }, [status, user]);

  // Reload profiles whenever auth user changes
  useEffect(() => {
    const uid = user?.id || null;
    if (lastUserIdRef.current === uid) return;
    lastUserIdRef.current = uid;
    if (!uid) {
      setProfiles([]);
      setActiveProfile(null);
      profileStore.clear();
      return;
    }
    fetchProfiles();
  }, [user?.id, fetchProfiles]);

  const switchProfile = useCallback((profile) => {
    if (!profile?.id) return;
    profileStore.set(profile.id);
    setActiveProfile(profile);
  }, []);

  const clearActiveProfile = useCallback(() => {
    profileStore.clear();
    setActiveProfile(null);
  }, []);

  const value = useMemo(
    () => ({
      profiles,
      activeProfile,
      loading,
      refreshProfiles: fetchProfiles,
      switchProfile,
      clearActiveProfile,
    }),
    [profiles, activeProfile, loading, fetchProfiles, switchProfile, clearActiveProfile],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used within ProfileProvider");
  return ctx;
}
